import React from 'react';
import ContentBox from '../components/ContentBox';

function TermsOfService() {
    const lastUpdated = '30 April 2026';

    const sections = [
        {
            title: 'Acceptance of Terms',
            content: [
                'By accessing or using Frankly Origins ("the Site"), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you must not use the Site.',
                'We reserve the right to update these terms at any time. Continued use of the Site following any changes constitutes your acceptance of the revised terms.',
            ],
        },
        {
            title: 'Eligibility',
            content: [
                'You must be at least 18 years of age to use Frankly Origins. By using the Site, you confirm that you meet this requirement.',
                'You must have a valid Habbo Origins account and a Discord account verified through OriginsBot to access member features on Frankly Origins.',
                'Frankly Origins is a fan site intended for players of Habbo Origins. It is not affiliated with, endorsed by, or connected to Sulake Corporation Oy in any way.',
            ],
        },
        {
            title: 'Accessing the Site',
            content: [
                'Access to Frankly Origins is granted via Discord OAuth through OriginsBot. By logging in, you authorise OriginsBot to share your Discord ID and verified Habbo username with us. No password is created or stored on Frankly Origins.',
                'You are responsible for maintaining the security of your Discord account. You agree to:',
                [
                    'Not share your session or allow others to use your account on this site.',
                    'Not attempt to access the site using another person\'s Discord account or Habbo identity.',
                    'Notify us immediately at hello@franklyorigins.net if you suspect unauthorised access to your account on this site.',
                    'Not use multiple accounts to circumvent bans, restrictions, or site features.',
                ],
            ],
        },
        {
            title: 'Acceptable Use',
            content: [
                'You agree not to use Frankly Origins to:',
                [
                    'Post or share content that is abusive, harassing, threatening, defamatory, obscene, or otherwise objectionable.',
                    'Impersonate another person or misrepresent your affiliation with any individual or organisation.',
                    'Attempt to gain unauthorised access to any part of the Site or its systems.',
                    'Spam, advertise, or promote third party services without our permission.',
                    'Engage in any activity that disrupts or interferes with the normal operation of the Site.',
                    'Scrape, harvest, or systematically extract data from the Site without prior written consent.',
                ],
                'We reserve the right to remove any content and suspend or permanently ban any account that violates these terms, at our sole discretion.',
                'Your use of the Site is also subject to our Community Guidelines, which set out the standards we expect for conduct, content, and image uploads. The Community Guidelines are incorporated into these Terms of Service by reference.',
            ],
        },
        {
            title: 'User Generated Content',
            content: [
                'By submitting content to Frankly Origins, including comments, profile information, image uploads, or any other contributions, you grant us a non-exclusive, royalty-free licence to display and use that content on the Site.',
                'You retain ownership of content you create. You are solely responsible for ensuring that content you submit does not infringe the rights of any third party.',
                'Images uploaded via FranklyCloud are scanned by a third-party AI moderation service before being accepted. Images that are flagged as inappropriate are rejected and not stored. Accepted images are stored on our servers and may be visible to other users and to site administrators.',
                'We do not actively monitor all user generated content but reserve the right to remove any content we deem inappropriate or in violation of these terms.',
            ],
        },
        {
            title: 'Intellectual Property',
            content: [
                'All original content on Frankly Origins, including but not limited to site design, written articles, graphics, and branding, is the property of Frankly Origins and may not be reproduced without permission.',
                'Habbo related imagery, furniture icons, and other Habbo assets displayed on this Site are the property of Sulake Corporation Oy. Frankly Origins is a fan site and claims no ownership over these materials.',
            ],
        },
        {
            title: 'Framps, Rewards and Virtual Items',
            content: [
                'Any framps, rewards, or virtual items earned or awarded on Frankly Origins are non-transferable, have no real-world monetary value, and are provided at our discretion.',
                'We reserve the right to modify, withdraw, or reset virtual items or rewards at any time without notice or compensation.',
            ],
        },
        {
            title: 'Disclaimers and Limitation of Liability',
            content: [
                'Frankly Origins is provided "as is" without warranties of any kind, express or implied. We do not guarantee that the Site will be available at all times, error-free, or free of harmful components.',
                'To the fullest extent permitted by law, Frankly Origins and its operators shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Site.',
            ],
        },
        {
            title: 'Termination',
            content: [
                'We may suspend or terminate your access to Frankly Origins at any time, with or without cause, and with or without notice. Upon termination, your right to use the Site ceases immediately.',
                'You may request account deletion at any time by contacting us at hello@franklyorigins.net.',
            ],
        },
        {
            title: 'Governing Law',
            content: [
                'These Terms of Service are governed by and construed in accordance with the laws of England and Wales. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
            ],
        },
        {
            title: 'Contact',
            content: [
                'If you have any questions about these Terms of Service, please contact us at hello@franklyorigins.net.',
            ],
        },
    ];

    const renderContent = (content) =>
        content.map((item, i) => {
            if (Array.isArray(item)) {
                return (
                    <ul key={i} className="list-disc list-inside space-y-1 ml-2 mt-1 mb-2">
                        {item.map((li, j) => (
                            <li key={j} className="text-gray-700 text-sm leading-relaxed">
                                {li}
                            </li>
                        ))}
                    </ul>
                );
            }
            return (
                <p key={i} className="text-gray-700 text-sm leading-relaxed mb-2">
                    {item}
                </p>
            );
        });

    return (
        <div className="w-full min-h-screen p-4 lg:p-6">
            <ContentBox theme="default" tint="gold" header="Terms of Service">
                <div className="mb-4 pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                        Last Updated: {lastUpdated}
                    </p>
                    <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                        Please read these Terms of Service carefully before using Frankly Origins. These terms govern your use of the Site and form a legal agreement between you and us.
                    </p>
                </div>

                <div className="space-y-5">
                    {sections.map((section, i) => (
                        <div key={i}>
                            <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-maroon mb-2">
                                {i + 1}. {section.title}
                            </h3>
                            <div>{renderContent(section.content)}</div>
                        </div>
                    ))}
                </div>
            </ContentBox>
        </div>
    );
}

export default TermsOfService;